import type { SolutionPageContent } from '../../types/solution'

export const sellAndGetPaidSolution: SolutionPageContent = {
  slug: 'sell-and-get-paid',
  hero: {
    headline: 'Get paid every way your customers want to pay',
    subheadline: 'Accept mobile money, card, cash and bank transfers, and see every payment in one place.',
    card: {
      type: 'progress',
      title: 'Payments today',
      badge: { label: 'RWF 612,400', tone: 'brand' },
      rows: [
        { label: 'Mobile money', value: 'RWF 368,000', pct: 60, tone: 'brand' },
        { label: 'Cash', value: 'RWF 142,400', pct: 23 },
        { label: 'Card', value: 'RWF 72,000', pct: 12 },
        { label: 'Bank transfer', value: 'RWF 30,000', pct: 5 },
      ],
    },
  },
  challenges: [
    {
      quote: 'Customers leave when I can’t take their payment method.',
      detail: 'Not everyone carries cash anymore, and I lose the sale.',
    },
    {
      quote: 'Tracking mobile money payments by hand takes hours.',
      detail: 'I scroll through SMS messages trying to match them to sales.',
    },
    {
      quote: 'Customers owe me money and I lose track.',
      detail: 'Small amounts on credit turn into big amounts I never collect.',
    },
  ],
  helps: [
    {
      solves: 'Customers leave when I can’t take their payment',
      title: 'Every payment method at checkout',
      copy: 'Take mobile money, card, cash, or bank transfer — or split one sale across several. Customers pay the way they want.',
      product: 'sales-pos',
      visual: { kind: 'screen', id: 'payments' },
    },
    {
      solves: 'Tracking mobile money by hand takes hours',
      title: 'Payments recorded automatically',
      copy: 'Every payment is matched to its sale the moment it’s made. No more scrolling through messages.',
      product: 'sales-pos',
      visual: {
        kind: 'card',
        card: {
          type: 'list',
          title: 'Recent payments',
          rows: [
            { label: 'Mobile money · 078•••4412', sublabel: 'Receipt #1842 · 10:14', value: 'RWF 12,500', tone: 'positive' },
            { label: 'Card · •••• 2291', sublabel: 'Receipt #1843 · 10:21', value: 'RWF 36,000', tone: 'positive' },
            { label: 'Cash', sublabel: 'Receipt #1844 · 10:26', value: 'RWF 4,800', tone: 'positive' },
          ],
          footer: { label: 'Matched to sales', value: '100%' },
        },
      },
    },
    {
      solves: 'Customers owe me money',
      title: 'Customer balances and credit',
      copy: 'Sell on credit with a clear record, set limits, and send balance reminders by SMS. Know exactly who owes what.',
      product: 'customers',
      visual: {
        kind: 'card',
        card: {
          type: 'list',
          title: 'Customers on credit',
          rows: [
            { label: 'Jean Bosco H.', sublabel: 'Limit RWF 50,000', value: 'RWF 32,000', tone: 'warning' },
            { label: 'Mama Keza shop', sublabel: 'Limit RWF 100,000', value: 'RWF 64,500' },
            { label: 'Olivier N.', sublabel: 'Paid 21 Sep', value: 'Settled', tone: 'positive' },
          ],
          action: 'Send reminders',
        },
      },
    },
    {
      solves: 'Tracking mobile money by hand takes hours',
      title: 'Daily sales and cash reconciliation',
      copy: 'At closing, see expected cash against what’s in the drawer, and totals for every payment method.',
      product: 'reports',
      visual: {
        kind: 'card',
        card: {
          type: 'list',
          title: 'Cash-up · 23 September',
          badge: { label: 'Balanced', tone: 'positive' },
          rows: [
            { label: 'Expected cash', value: 'RWF 142,400' },
            { label: 'Counted cash', value: 'RWF 142,400' },
            { label: 'Mobile money', value: 'RWF 368,000' },
            { label: 'Card + bank', value: 'RWF 102,000' },
          ],
          footer: { label: 'Difference', value: 'RWF 0' },
        },
      },
    },
  ],
  journey: {
    heading: 'How it works',
    subheading: 'From your first product to your first report.',
    items: [
      { title: 'Add your products', copy: 'Import a list or add items with prices in minutes.' },
      { title: 'Sell in store or on your phone', copy: 'Use a tablet at the counter or your phone on the go.' },
      { title: 'Customer pays their way', copy: 'Mobile money, card, cash, or bank transfer.' },
      { title: 'Payment recorded automatically', copy: 'Matched to the sale, with a receipt sent.' },
      { title: 'See it in your reports', copy: 'Daily totals by method, ready for cash-up.' },
    ],
  },
  results: [
    { value: '+20%', label: 'more sales from accepting every payment method' },
    { value: '3 hrs', label: 'saved each week matching payments' },
    { value: '2×', label: 'faster collection of customer balances' },
  ],
  testimonial: {
    quote: 'Most of my customers pay by mobile money now. Kayko records every payment against the sale, so I don’t chase anything.',
    name: 'Héritier Muhire',
    role: 'Founder',
    business: 'YEET Coffee',
    location: 'Kimihurura, Kigali',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/c04828fc-7f33-4022-bb39-af6bdb618150.jpg',
    metric: { value: '3 hrs', label: 'saved every week on payment tracking' },
  },
  products: ['sales-pos', 'customers', 'reports'],
  plan: { name: 'Starter', audience: 'businesses that take several payment methods' },
  faq: [
    {
      question: 'Which payment methods can I accept?',
      answer: 'Mobile money, card, cash, and bank transfer. You can also split one sale across several methods. Multiple payment methods are included from the Starter plan.',
    },
    {
      question: 'Are there payment provider fees?',
      answer: 'Kayko doesn’t add a transaction fee. Mobile money, card, and bank providers may charge their own fees, which vary by method.',
      link: { label: 'See the full fee schedule', href: '/legal/fees' },
    },
    {
      question: 'When does the money arrive?',
      answer: 'Mobile money and cash are available right away. Card and bank transfer settlement times depend on the provider [to confirm].',
    },
    {
      question: 'Can I send digital receipts?',
      answer: 'Yes. Send an EBM-compliant receipt by SMS or email, or print it at the counter.',
    },
    {
      question: 'Can I sell on credit?',
      answer: 'Yes. Record credit sales against a customer, set a limit, and track payments until the balance is settled.',
    },
  ],
  related: ['retail', 'services', 'understand-performance'],
  cta: {
    headline: 'Never lose a sale to the wrong payment method',
    subheadline: 'Start accepting every payment today. Local support in Kigali when you need it.',
  },
}
